import type { PaymentAction, PaymentMethodsResponse } from '@adyen/adyen-web';
import type { AxiosRequestConfig } from 'axios';

import type { DeliveryMethods } from '~/api/ETA';
import { company } from '~/company/Company';
import type { DestinationKind } from '~/utils/destinationKind';
import { normalizeDeliveryMethod } from '~/utils/normalizeDeliveryMethod';

import type { OrderItemBundle } from './Catalog';
import type { Customer } from './Customer';
import { ENDPOINT } from './constants';
import type { ETAPaymentMethod } from './ETA';
import type { IPromocode } from './Promocodes';
import { RequestAPI } from './Requests';
import { getLangHeaders } from './utils';

export type NewOrderCartItem = Omit<
  OrderCartItem,
  'id' | 'order_id' | 'created_at' | 'updated_at' | 'status' | 'actual_quantity'
>;

export type OrderCartItemStatus =
  | 'ordered'
  | 'reserved'
  | 'reserved_partially'
  | 'picked_partially'
  | 'missing'
  | 'picked'
  | 'out_of_stock';

export type OrderPublicStatus =
  | 'accepted'
  | 'picking'
  | 'ready_to_ship'
  | 'ready_for_pickup'
  | 'in_delivery'
  | 'delivered'
  | 'failed'
  | 'cancelled'
  | 'not_collected';

export type OrderStatus =
  | 'created'
  | 'reserved'
  | 'confirmed'
  | 'delivery_created'
  | 'wh_created'
  | 'picking'
  | 'ready_to_ship'
  | 'in_delivery'
  | 'in_logistic'
  | 'delivered'
  | 'delivered_partially'
  | 'finished'
  | 'cancelled'
  | 'delivery_failed';

export enum PaymentSystem {
  stripe = 'stripe',
  myfatoorah = 'myfatoorah',
  midtrans = 'midtrans',
  amazon = 'amazon',
  adyen = 'adyen',
  verifone = 'verifone',
  pvit = 'pvit',
  cash = 'cash',
  octo = 'octo',
  moneta = 'moneta',
  postpayment = 'postpayment',
}

const SETTLED_OFFLINE_PAYMENT_SYSTEMS: PaymentSystem[] = [
  PaymentSystem.cash,
  PaymentSystem.postpayment,
];

export const isSettledOfflineSystem = (system: PaymentSystem): boolean =>
  SETTLED_OFFLINE_PAYMENT_SYSTEMS.includes(system);

export interface PaymentSystemItem {
  system: PaymentSystem;
  methods: ETAPaymentMethod[];
}

export interface OrderCartItem {
  id: string;
  order_id: string;
  created_at: string;
  updated_at: string;
  status: OrderCartItemStatus;
  sku: string;
  requested_quantity: number;
  actual_quantity: number;
  picked_quantity: number | null;
  /** Quantity already refunded for this item. TODO(returns): provided by OMS once epic ships; defaults to 0. */
  refunded_quantity?: number;
  /** Quantity already requested in other returns for this item. TODO(returns): provided by OMS once epic ships; defaults to 0. */
  returned_quantity?: number;
  weight: number;
  base_price: number;
  discount_amount: number;
  paid_price: number;
  /** `base_price` converted to the company currency (converted-price companies — DubaiExpress). */
  converted_base_price?: number | null;
  /** Legacy name of `converted_base_price`; still returned by older backends. */
  sale_price?: number | null;
  /** `paid_price` converted to the company currency (converted-price companies — DubaiExpress). */
  converted_discount_price?: number | null;
  product_id: string;
  name: string;
  image: string;
  price_per_unit: string;
  promocode_discount_amount: number;
  paid_bonuses: number;
  promo_required_quantity?: number | null;
  promo_quantity_discount_price?: number | null;
  bundle_id?: string | null;
  is_gift?: boolean;
  /** Per-item customs duty in base-currency minor units (parcel-from-Dubai / DubaiExpress). */
  customs_duties_fee?: number | null;
  /** Per-item customs duty converted to the company currency. */
  converted_customs_duties_fee?: number | null;
  /** Per-item dimensions in cm. */
  length?: number | null;
  width?: number | null;
  height?: number | null;
}

export interface OrderAddress {
  id: string;
  order_id: string;
  city: string;
  country_id: string;
  region: string;
  address_1: string | null;
  address_2: string | null;
  latitude: number | null;
  longitude: number | null;
  notes: string | null;
  instructions: string[] | null;
  comment: string | null;
  zip: string;
  customer_address_id: string;
  building: string;
  apartment: string;
  floor: string;
  type: string;
  place_id: string;
}

interface OrderSla {
  start_after: string;
  ship_by: string;
  deliver_by_min: string;
  deliver_by_max: string;
}

interface Recipient {
  id: string;
  order_id: string;
  full_name: string;
  email?: string;
  phone: string;
}

interface OrderDelivery {
  tracking?: {
    pickup?: string | null;
    dropoff?: string | null;
  };
}

/**
 * Pickup point info attached to an order eligible for return.
 * `null` when the order is not PUDO / cannot be returned through this flow.
 * Source: `GET /v2/orders/:id` (returns-flow epic 09).
 */
export interface OrderReturnPickupPoint {
  /** Pickup-point code (= warehouse code). */
  code: string;
  /** Postal address of the pickup point. */
  address: string;
  /** Geo coordinates as "lat,lng". */
  location: string;
  /** Working hours of the pickup point, e.g. "11:00 - 22:00". TODO(returns): provided by OMS; rendered only when present. */
  working_hours?: string | null;
}

/**
 * Pickup point (ПВЗ) an LMS order is delivered to. Present on orders with
 * `destination_kind === 'pup'`; `code` equals the wms-connector `externalId`.
 */
export interface OrderPickupPoint {
  id: string;
  code: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

/** Status of an individual Return (mirrors `ReturnStatus` in OMS). */
export type ReturnStatus =
  | 'created'
  | 'at_pickup_point'
  | 'accepted_fully'
  | 'accepted_partially'
  | 'rejected'
  | 'refunded'
  | 'finished'
  | 'cancelled';

/**
 * Aggregated info about Return'ы по заказу.
 * Source: `GET /v2/orders/:id` (returns-flow rev 3).
 * `order.status` does NOT change when a Return is created, so this block is
 * the only signal that a return is in progress.
 */
export interface OrderReturnsSummary {
  /** Number of non-terminal Returns (terminal: finished, cancelled). */
  active_count: number;
  /** Total number of Returns ever created for the order. */
  total_count: number;
  /** Status of the most recently created Return, or null when there are none. */
  latest_status: ReturnStatus | null;
  /** Convenience flag, equivalent to `active_count > 0`. */
  is_in_progress: boolean;
  /** ISO deadline by which items must be brought to the pickup point. TODO(returns): provided by OMS; rendered only when present. */
  return_deadline?: string | null;
}

interface OrderPicking {
  id: string;
  picker_id: null;
  oms_order_id: string;
  transfer_id: number;
  transfer_type: string;
  odoo_request_id: string;
  warehouse_code: string;
  sla_start_after: string;
  sla_ship_by: string;
  startedAt: null;
  completedAt: null;
  status: string;
  createdAt: string;
  updatedAt: string;
  picker: null;
}

export interface OrderFiscalReceipt {
  attempts: number;
  created_at: string;
  error: Nullable<string>;
  fiscalReceiptId: number;
  fiscalSign: string;
  message: string;
  qrCodeUrl: string;
  receiptSeq: number;
  status: 'SUCCESS' | 'FAILED';
  terminalId: string;
  updated_at: string;
}

/**
 * Payment metadata returned by the backend in OrderPayment.
 * Contains payment-system-specific fields. All fields are optional because
 * different providers return different shapes (octo populates card_* / *Pan,
 * other providers may only return qrCode, etc).
 */
export interface OrderPaymentMetadata {
  qrCode?: string;
  card_type?: string;
  card_vendor?: string;
  /** Masked PAN, e.g. "403396** **** 0646" */
  maskedPan?: string;
  fiscal_receipt?: OrderFiscalReceipt;
  fiscal_receipt_url?: string;
  fiscal_refunds?: OrderFiscalReceipt[];
}

interface OrderPayment {
  order_id: string;
  status: string;
  payment_method: Nullable<ETAPaymentMethod>;
  payment_system: PaymentSystem;
  payment_metadata: OrderPaymentMetadata;
  created_at: string;
  /** Amount charged — for a split order, across the whole customer order. */
  amount?: number;
}

export interface OrderShort {
  id: string;
  status: OrderStatus;
  created_at: string;
  updated_at: string;
  customer_id: string;
  delivery_id: null;
  payment_id: null;
  order_id: string;
  is_imported: boolean;
  warehouse_code: string;
  currency: string;
  warehouse_id: string;
  has_feedback: boolean;
  delivery_state: null;
  items: OrderCartItem[];
  receipt_url: string | null;
  address: OrderAddress | null;
  recipient: Recipient | null;
  public_status: OrderPublicStatus;
  sla: OrderSla;
  promocode: string | null;
  base_total: number | null;
  promocode_discount: number | null;
  discount_total: number | null;
  /** Member (CityDrinks Club) part of `discount_total`, in minor units; `null` without one. */
  membership_discount_total?: number | null;
  /** Percent of the member step applied to the order; `null` when not stated. */
  membership_tier_percent?: number | null;
  /** Member discount plus delivery saved by the club, in minor units; `null` without one. */
  membership_saving_total?: number | null;
  /** Delivery fee saved by the club, in minor units; `null` without one. */
  membership_delivery_saving?: number | null;
  paid_total: number | null;
  /** `base_total` converted to the company currency (converted-price companies — DubaiExpress). */
  converted_base_total?: number | null;
  /** Legacy name of `converted_base_total`; still returned by older backends. */
  converted_total?: number | null;
  /** `discount_total` converted to the company currency. */
  converted_discount_total?: number | null;
  /** Amount actually charged, converted to the company currency — what the customer paid. */
  converted_paid_total?: number | null;
  /** `promocode_discount` converted to the company currency. */
  converted_promocode_discount?: number | null;
  /** `shipping_cost` (base delivery price, no discounts) converted to the company currency. */
  converted_shipping_cost?: number | null;
  /** Delivery threshold/promo discount converted to the company currency. */
  converted_delivery_discount_amount?: number | null;
  /** Delivery promocode discount converted to the company currency. */
  converted_delivery_promocode_discount_amount?: number | null;
  /** Final delivery price the customer pays, converted to the company currency. */
  converted_delivery_price?: number | null;
  /** `customs_duties_fee` converted to the company currency. */
  converted_customs_duties_fee?: number | null;
  /** `customs_service_fee` converted to the company currency. */
  converted_customs_service_fee?: number | null;
  /** `registration_fee` converted to the company currency. */
  converted_registration_fee?: number | null;
  actual_paid_loyalty_points: number | null;
  delivery_price: number | null;
  is_delivery_free: boolean | null;
  save_card: boolean;
  service_fee: number;
  cancel_reason: 'CANCEL_BY_USER' | null;
  delivery_method: DeliveryMethods;
  distance_to_warehouse: number;
  warehouse_location_latitude: number;
  warehouse_location_longitude: number;
  received_bonuses: number;
  payment_method: Nullable<ETAPaymentMethod>;
  payment: OrderPayment;
  tax_percent?: number;
  tax_amount?: number;
  bundles?: OrderItemBundle[];
  itemsCount: {
    actual: number;
    requested: number;
  };
  actual_paid_price: number;
  actual_paid_price_money: number;
  paid_external_bonuses: number;
  actual_tax_amount: number;
  delivery_tracking_url?: string;
  delivery_zone_id: string | null;
  delivery_zone_name: string | null;
  /**
   * Nested delivery info as returned by the orders-list endpoint. The list
   * response keeps `delivery_zone_id` / `delivery_zone_name` here, while the
   * detail response promotes them to the top level — resolve from both.
   */
  order_delivery?: {
    warehouse_address?: string | null;
    warehouse_location?: string | null;
    delivery_zone_id?: string | null;
    delivery_zone_name?: string | null;
  };
  /** When the order will be cancelled (only when status === 'created' or 'reserved') */
  cancel_at?: string;
  /**
   * Pickup-point info for the returns flow. Present on `GET /v2/orders/:id`.
   * `null` when the order is not eligible for return (e.g. courier delivery).
   */
  return_pickup_point?: OrderReturnPickupPoint | null;
  /**
   * Aggregated info about Return'ы по заказу. Present on `GET /v2/orders/:id`.
   * `order.status` does not reflect refunds — use this block to detect that a
   * return is in progress (badge / hide «Вернуть товар» button).
   */
  returns_summary?: OrderReturnsSummary | null;
  registration_fee?: number;
  customs_service_fee?: number;
  customs_duties_fee?: number;
  /** UUID of the external pickup point (ПВЗ) the order is delivered to. */
  pickup_point_uuid?: string | null;
  /** LMS pickup point the order is delivered to (`destination_kind === 'pup'`). */
  pickup_point?: OrderPickupPoint | null;
  pup_type?: 'partner' | null;
  storage_until?: string | null;
  delivery_tracking_number?: string | null;
  /** Partner warehouse fulfilling this order; null for the main account. */
  supplier_partner_code?: string | null;
  /** Set only on orders split across partner warehouses. */
  multi_order?: OrderMultiOrder | null;
  /** Set only on orders split across partner warehouses. */
  delivery_group?: OrderDeliveryGroup | null;
  /** Id of the parent customer order of a split order. */
  multi_order_id?: string | null;
  /** Customer-facing status of this shipment (JS-14623). Absent on older backends. */
  customer_status?: CustomerStatus | null;
  /** Status of the whole customer order across its shipments (JS-14623). */
  customer_order_summary?: CustomerOrderSummary | null;
  /** When the shipment was delivered. */
  delivered_at?: string | null;
  /** Until when a pickup-point shipment is kept for the customer. */
  storage_until?: string | null;
  /**
   * Where the order is delivered to — the customer's address or a pickup
   * point. Sent by the client at checkout and stored with the order, so the
   * fulfilment type never has to be guessed from the delivery zone.
   */
  destination_kind?: DestinationKind | null;
  /**
   * The pickup window the order was booked into, when self-collection was
   * scheduled through a bookable slot rather than picked up ASAP.
   */
  slot_delivery_details?: {
    current_date: number;
    schedule_slot_id: string;
  } | null;
}

/** Parent record of a customer order split across partner warehouses. */
/**
 * Customer-facing status of one shipment (fulfilment order), computed by the
 * LMS from the events of the order's fulfilment flow (JS-14623).
 */
export type CustomerStatusCode =
  | 'AWAITING_PASSPORT'
  | 'ORDER_CONFIRMED'
  | 'PREPARING'
  | 'READY_FOR_SHIPMENT'
  | 'ON_THE_WAY'
  | 'OUT_FOR_DELIVERY'
  | 'READY_FOR_PICKUP'
  | 'DELIVERED'
  | 'CANCELLED';

/** Where a customer-facing status came from. */
export type CustomerStatusSource = 'lms' | 'legacy';

/** Customer-facing status of a shipment with the comment on what is happening to it. */
/** One earlier status or comment of a shipment, as the route history lists it. */
export interface CustomerStatusEvent {
  code: CustomerStatusCode;
  label: string;
  comment_key: string | null;
  comment: string | null;
  /** ISO datetime of the event. */
  occurred_at: string;
}

export interface CustomerStatus {
  code: CustomerStatusCode;
  /** English status name; fallback when the code has no translation. */
  label: string;
  /** Translation key of the comment (`gbs.<CODE>`, `tswms.*`, `ds.*`, `passport.requested`, `lms.cancelled`). */
  comment_key: string | null;
  /** English comment; fallback for an untranslated key. `null` means nothing to show. */
  comment: string | null;
  /** Interpolation params of the comment. Not used yet. */
  comment_params: Record<string, string | number> | null;
  /** The customer has to do something; the comment says what. */
  action_required: boolean;
  /** ISO datetime of the last status change. */
  updated_at: string;
  /** ISO datetime of the last comment change; the status may have changed earlier. */
  comment_updated_at?: string | null;
  /** Earlier statuses and comments, oldest first; only the detailed order carries them. */
  history?: CustomerStatusEvent[];
  /** Fulfilment flow; not needed for display. */
  flow: 'STANDARD_FF' | 'DS_WMS' | 'DS_DIRECT' | null;
  /** `legacy` statuses are derived from `public_status` and carry no comment. */
  source: CustomerStatusSource;
}

/** How the shipments of a customer order combine into one headline. */
export type CustomerOrderSummaryKind =
  | 'single'
  | 'common'
  | 'in_shipments'
  | 'partially_delivered'
  | 'delivered'
  | 'cancelled';

/** One shipment of a customer order as listed in `CustomerOrderSummary`. */
export interface CustomerOrderShipment {
  order_id: string;
  code: CustomerStatusCode;
}

/** Customer-order level status across every shipment it was split into. */
export interface CustomerOrderSummary {
  kind: CustomerOrderSummaryKind;
  /** Shared status of the shipments; `null` when they differ. */
  status: CustomerStatusCode | null;
  shipments_total: number;
  /** Shipments that are not cancelled. */
  active_count: number;
  delivered_count: number;
  cancelled_count: number;
  /** `co.<kind>` */
  text_key: string;
  /** Interpolation params of the headline, e.g. `{ delivered, active }`. */
  text_params: Record<string, number>;
  /** English headline; fallback for an untranslated key. */
  text: string;
  shipments: CustomerOrderShipment[];
}

export interface OrderMultiOrder {
  id: string;
  /** Number the customer knows the order by, e.g. "GTG5511". */
  order_id: string;
  company_id: string;
  idempotency_key: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/**
 * Parcels a split order is delivered in. Delivery is charged once for the whole
 * group, while every parcel keeps its own status and tracking.
 */
export interface OrderDeliveryGroup {
  id: string;
  delivery_price: number;
  /** `delivery_price` converted to the company currency. */
  converted_delivery_price: number | null;
  orders: OrderShort[];
}

export interface OrderWithPromocode extends OrderShort {
  promocode_data: IPromocode;
}

export interface Order extends OrderShort {
  delivery: OrderDelivery;
  picking: OrderPicking;
  customer: Customer[];
  payment: OrderPayment;
  finishedMoreThanDayAgo: boolean;
  warehouse_address: string;
}
export interface AwsPayment {
  id: string;
  public_identifier: string;
  payment_system_id: string;
  payment_system_access: string;
  customer_id: string;
  company_id: string;
  created_at: string;
  is_default: boolean;
}
export interface Payment {
  card: {
    brand: string;
    checks: {
      address_line1_check: 'pass' | null;
      address_postal_code_check: 'pass' | null;
      cvc_check: 'pass' | null;
    };
    country: string;
    exp_month: number;
    exp_year: number;
    fingerprint: string;
    last4: string;
  };
  id: string;
  paymentToken: string | null;
  metadata: {
    is_default: string;
  };
}

/**
 * Payload for binding a bank card via octo/uzcard/humo integration.
 */
export interface BindCardRequest {
  /** Card PAN, 16 digits without spaces */
  pan: string;
  /** Expiration date in YYMM format (e.g. "2805" for May 2028) */
  exp: string;
  /** Cardholder name as printed on the card */
  cardHolderName: string;
  /** Cardholder phone, digits only (e.g. "990000000000") */
  phone: string;
  /** CVC2 code (3 digits) */
  cvc2: string;
  /** Payment system method (e.g. "humo", "uzcard"); optional */
  method?: string;
  /** Delivery zone id for which the card is being bound; optional */
  deliveryZoneId?: string;
}

/**
 * SMS verification metadata returned by the bind-card endpoint.
 */
export interface BindCardVerification {
  /** Verification session id used to confirm SMS code */
  verifyId: number;
  /** Masked phone the SMS was sent to (e.g. "+998** *****33") */
  phone: string;
  /** Seconds left until the SMS expires on backend */
  secondsLeft: number;
}

/**
 * Which OTP flow the backend selected for the binding session, based on PAN:
 * - `partner_otp`: Humo/Uzcard — OTP collected via /verification + /check-sms-key in our UI.
 * - `hosted_otp`: Visa/Mastercard — OTP collected on OCTO's hosted page (open `redirectUrl` in iframe).
 */
export type BindCardFlow = 'partner_otp' | 'hosted_otp';

/**
 * Response from the bind-card endpoint.
 */
export interface BindCardResponse {
  /** Binding session uuid used in the follow-up SMS verification */
  bindingUuid: string;
  /** URL of the hosted 3DS/binding page; opened in iframe for hosted_otp flow */
  redirectUrl: string;
  /** Flow selected by the backend based on PAN */
  flow: BindCardFlow;
  /** Provider-specific metadata */
  additionalData: {
    shop_transaction_id: string;
  };
}

/**
 * Status of the bind-card SMS verification, returned by the polling endpoint.
 * - `pending`: SMS is being prepared on the provider side, keep polling.
 * - `ready`: verification metadata available, show SMS form.
 * - `expired`: secondsLeft is zero, show "resend" UI.
 * - `failed`: terminal failure with explanation.
 * - `hosted_otp`: this binding session is Visa/MC; switch to WebView with `redirectUrl`.
 */
export type BindCardVerificationStatus =
  | { status: 'ready'; verification: BindCardVerification }
  | { status: 'pending' }
  | { status: 'expired'; verification: BindCardVerification }
  | { status: 'failed'; error: string }
  | { status: 'hosted_otp'; redirectUrl: string };

/**
 * Payload for confirming SMS code during card binding.
 */
export interface CheckSmsKeyRequest {
  /** Binding session uuid returned by bind-card */
  bindingUuid: string;
  /** SMS code entered by the user */
  smsKey: string;
  /** Verification session id returned by bind-card */
  verifyId: number;
  /** Delivery zone id for which the card is being bound; optional */
  deliveryZoneId?: string;
}

export interface OctoPaymentMethod {
  id: string;
  card_brand: string;
  card_last_four: string;
  card_exp_month: number;
  card_exp_year: number;
  card_holder_name: string;
  is_default: boolean;
}

export interface VerifonePaymentCard {
  id: string;
  customer_id: string;
  company_id: string;
  provider: string;
  provider_token: string;
  card_brand: string;
  card_last_four: string;
  card_exp_month: number;
  card_exp_year: number;
  card_holder_name: string | null;
  card_type: string;
  token_expiry_date: string;
  stored_credential_reference: string;
  scheme_reference: string;
  token_scope: string;
  metadata: {
    id: string;
    arn: string;
    stan: string;
    amount: number;
    status: string;
    blocked: boolean;
    details: {
      auto_capture: boolean;
    };
    created_by: string;
    cvv_result: string;
    cvv_present: boolean;
    reason_code: string;
    card_details: {
      expiry_year: number;
      expiry_month: number;
      masked_card_number: string;
    };
    token_details: {
      bin: string;
      type: string;
      brand: string;
      variant: string;
      last_four: string;
      created_at: string;
      updated_at: string;
      expiry_year: number;
      issuer_name: string;
      reuse_token: string;
      token_scope: string;
      expiry_month: number;
      token_status: string;
      currency_code: string;
      issuer_country: string;
      token_expiry_date: string;
    };
    balance_amount: number;
    additional_data: {
      initiator_trace_id: string;
    };
    payment_product: string;
    reversal_status: string;
    scheme_reference: string;
    stored_credential: {
      reference: string;
      processing_model: string;
      stored_credential_type: string;
    };
    merchant_reference: string;
    shopper_interaction: string;
    threed_authentication: {
      cavv: string;
      eci_flag: string;
      enrolled: string;
      pares_status: string;
      threeds_version: string;
      ds_transaction_id: string;
    };
    promo_financing_result: Record<string, unknown>;
    payment_provider_contract: string;
  };
  is_default: boolean;
  created_at: string;
  updated_at: string;
  isDefault?: boolean;
}

export interface GiftMechanism {
  id: string;
  cms_id: number;
  name: string;
  minimum_order_amount: number;
  gift_items: GiftItem[];
}

export interface GiftItem {
  id: number;
  name: string;
  code: string;
  price?: number;
  preview_image: string;
  preview_image_thumb: string;
}

export interface FillFeedbackRequest {
  rating: number;
  delivery_rating: number;
  product_variety_rating: number;
  product_quality_rating: number;
  comment: string;
}

export interface NewOrderResponseOLD {
  data: {
    multi_order?: {
      idempotency_key: string;
      company_id: string;
      order_id: string;
      created_at: number;
      updated_at: number;
      id: string;
    };
    orders: Array<{
      order: {
        id: string;
        order_id: string;
        duration: {
          min: number;
          max: number;
        };
        seller: string;
        payment_method: string;
        company_id: string;
      };
      payment_intent: {
        client_secret?: string;
      };
      payment: {
        id: string;
        myFatoorahPayment: {
          invoiceId?: number;
          paymentURL?: string;
          error?: boolean;
        };
      };
      payed_delivery: {
        shipping_cost: number;
        cost_threshold: number;
      };
      errors?:
        | boolean
        | Array<{
            message?: string;
            payload?: {
              sku: string;
              warehouse_code: string;
            };
          }>;
    }>;
  };
}

export interface NewOrderResponse {
  data: {
    multi_order?: {
      idempotency_key: string;
      company_id: string;
      order_id: string;
      created_at: number;
      updated_at: number;
      id: string;
    };
    orders: Array<{
      order: {
        id: string;
        order_id: string;
        duration: {
          min: number;
          max: number;
        };
        seller: string;
        payment_method: string;
        company_id: string;
        cancellationDelay: number;
      };
      payment_intent: {
        client_secret?: string;
      };
      payment: {
        id: string;
        providerPaymentDetails?: {
          paymentSystem: string;
          providerPaymentId: string;
          paymentLink?: string;
          paymentSession: string;
          additionalData?: {
            action: PaymentAction;
            acs_url?: string;
            payload?: string;
            transaction_id?: string;
          };
        };
        midtransPayment?: {
          id: string;
          redirect_url: string;
          token: string;
        };
        myFatoorahPayment: {
          invoiceId?: number;
          paymentURL?: string;
          error?: boolean;
        };
      };
      payed_delivery: {
        shipping_cost: number;
        cost_threshold: number;
      };
      errors?:
        | boolean
        | Array<{
            message?: string;
            payload?: {
              sku: string;
              warehouse_code: string;
            };
          }>;
    }>;
  };
}

export interface OrdersResponse {
  orders: OrderShort[];
  total_count: number;
}

export interface OrdersWithPromocodesResponse {
  data: OrderWithPromocode[];
  pagination: {
    total: number;
    page: number;
    limit: number;
  };
}

interface OrderResponse {
  order: Order;
}

export type Aws3DsStatus = 'waiting_3ds' | 'cancelled' | 'charged' | 'created';

export interface CheckPromocodeResponse {
  id: number;
  type: 'PERCENTAGE' | 'FIXED' | 'FREE_SHIPPING' | 'BUY_X_GET_Y';
  code: string;
  filters: Unnecessary<Record<string, unknown>>;
  value: number;
  applyToDiscountedItems: boolean;
  minimumPurchase: number;
  minimumQuantity: number;
  usageLimitCustomer: number;
  usageCurrent: number;
  usageLimitAll: number;
  dateRange: [string, string];
  orderNumberRange: [number, number];
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  productDiscountQuantity: number;
  discountTargets: null;
  /** Owner customer id for personal (per-customer) promocodes; null for public ones. */
  customerId?: string | null;
}

interface GiftResponse {
  data?: GiftMechanism;
}

interface CheckPayedTipResponse {
  status?: 'created' | 'received';
  errors?: string[];
}

export type NewOrderCartItemBundle = {
  id: number;
  requested_quantity: number;
};

export type NewOrderCartItemOffer = {
  base_price: number;
  discount_amount: number;
  image: string;
  name: string;
  paid_bonuses: number;
  paid_price: number;
  price_per_unit: string;
  product_id: string;
  promocode_discount_amount: number;
  requested_quantity: number;
  sku: string;
  weight: number;
};

export type NewOrderDelivery = {
  base_total: number;
  delivery_price: number;
  discount_total: number;
  is_delivery_free: boolean;
  paid_bonuses: number;
  paid_total: number;
  promocode: string | null;
  promocode_discount: number;
  service_fee: number;
  should_use_loyalty_points: boolean;
  should_use_bonuses: boolean;
  tips_amount: number;
  items: NewOrderCartItemOffer[];
  bundles?: NewOrderCartItemBundle[];
  slot_delivery_details?: {
    current_date: number;
    schedule_slot_id: string;
  };
};

export type NewOrderContent = {
  client_source: string;
  currency: string;
  customer_id: string;
  delivery_method: string;
  delivery_provider?: string;
  delivery_mechanic?: string;
  warehouse_code: string;
  /**
   * Mandatory once `delivery_provider` is set — the backend then reads the
   * order as v3 and prices it by this zone.
   */
  zone_id?: string;
  save_card: boolean;
  session_id?: string;
  /** UUID of the external pickup point (ПВЗ) chosen for the order. */
  pickup_point_uuid?: string;
  /**
   * Fulfilment type the customer picked. Drives zone selection on the backend
   * for companies where PUDO and courier delivery share one `delivery_method`.
   */
  destination_kind?: DestinationKind;
  /** Set for a partner (EMU) pickup point; own pickup points leave it unset. */
  pup_type?: 'partner';
  /** `external_code` of the partner pickup point the order is delivered to. */
  pup_external_id?: string;
  pup_provider?: string;
  pup_id?: string;
  deliveries: Array<NewOrderDelivery>;
  payment?: {
    pay_by_saved_card?: boolean;
    card_id?: Nullable<string>;
    payment_method: Nullable<ETAPaymentMethod>;
  };
};

interface OrderRequestInput {
  address: Omit<OrderAddress, 'id' | 'order_id'>;
  recipient: Omit<Recipient, 'id' | 'order_id'>;
  order: NewOrderContent;
}

interface CurrencyResponse {
  data: {
    currency: string;
    digits_after_coma: number;
  };
}

interface OrderPaymentDetails {
  payment_id: string;
  details: unknown;
}

export interface MyfatoorahInit {
  SessionId: string;
  CountryCode: string;
}

interface Verifone3dsData {
  Amount: number;
  AuthorizationPayload: string;
  CAVV: string;
  CurrencyCode: string;
  ECIFlag: string;
  PAResStatus: string;
  SignatureVerification: string;
  ThreeDSVersion: string;
}

interface Verifone3dsConfirmDto {
  details: {
    encryptedCard: string;
    threedAuthentication: Verifone3dsData;
  };
  payment_id: string;
}

interface OrderRequestsInterface {
  newOrder(data: OrderRequestInput, key: string): Promise<NewOrderResponse>;

  orderPaymentDetails(data: OrderPaymentDetails): Promise<void>;

  orderPaid(orderId: string): Promise<{ status: string }>;

  orderCancel(orderId: string): Promise<{ status: string }>;

  orderIdempotencyCancel(data: {
    idempotency_key: string;
  }): Promise<{ status: string }>;

  getOrders(data: {
    filter?: {
      customer_id?: string;
      status?: OrderStatus[];
    };
    options?: Record<string, any>;
  }): Promise<OrdersResponse>;

  getOrder(id: string): Promise<OrderResponse>;

  get3dsStatus(id: string): Promise<{
    '3ds_url': string;
    status: Aws3DsStatus;
  }>;

  verifone3ds(): Promise<{ jwt: string }>;

  verifone3dsConfirm(data: Verifone3dsConfirmDto): Promise<any>;

  getPayments(
    data: PaymentSystem[],
    deliveryZoneId?: string,
  ): Promise<
    Payment[] | AwsPayment[] | PaymentMethodsResponse | OctoPaymentMethod[]
  >;

  createIntent(): Promise<Payment>;

  addPayment(data: { payment_method_id: string }): Promise<Payment>;

  deletePayment(data: {
    payment_method_id: string;
    deliveryZoneId?: string;
  }): Promise<Payment>;

  setDefaultPayment(data: {
    payment_method_id: string;
    deliveryZoneId?: string;
  }): Promise<Payment>;

  bindCard(data: BindCardRequest): Promise<BindCardResponse>;

  getBindCardVerification(
    bindingUuid: string,
  ): Promise<BindCardVerificationStatus>;

  checkSmsKey(data: CheckSmsKeyRequest): Promise<void>;

  newFeedback(data: {
    rating: number;
    order_id: string;
  }): Promise<{ id: string }>;

  fillFeedback(feedbackId: string, data: FillFeedbackRequest): Promise<void>;

  checkPromocode(
    promocode: string,
    options?: AxiosRequestConfig,
  ): Promise<CheckPromocodeResponse>;

  getGifts(warehouseCode: string): Promise<GiftResponse>;

  payTip(
    orderId: string,
    data: {
      amount: number;
      save_card: boolean;
      currency: string;
      email: string;
    },
  ): Promise<{ client_secret: string }>;

  checkPayedTip(orderId: string): Promise<CheckPayedTipResponse>;

  getCurrency(): Promise<CurrencyResponse>;

  getPaymentSystems(deliveryZoneId?: string): Promise<PaymentSystemItem[]>;

  myfatoorahInit(): Promise<MyfatoorahInit>;

  getOrdersWithPromocodes(data: {
    page: number;
    limit: number;
  }): Promise<OrdersWithPromocodesResponse>;
}

/**
 * Orders placed before the app adopted the ETA v3 vocabulary come back with the
 * old delivery method name. Normalised here, at the edge, so nothing downstream
 * has to know two names for one method.
 */
const withNormalizedDeliveryMethod = <
  T extends { delivery_method: DeliveryMethods },
>(
  order: T,
): T =>
  order
    ? {
        ...order,
        delivery_method: normalizeDeliveryMethod(order.delivery_method),
      }
    : order;

export const OrderRequests: OrderRequestsInterface = {
  newOrder: (data, key) =>
    RequestAPI.post(ENDPOINT.orders.new, data, {
      headers: {
        'Idempotency-Key': key,
        ...getLangHeaders().headers,
      },
    }),

  orderPaid: (orderId) => RequestAPI.post(ENDPOINT.orders.paid + orderId),

  orderPaymentDetails: (data) =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.details, data),

  get3dsStatus: (orderId) =>
    RequestAPI.get(ENDPOINT.orders.paymentMethods.status3ds + orderId),

  verifone3ds: () =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.verifone3ds, {
      companyId: company.config.id,
    }),

  verifone3dsConfirm: (data) =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.verifone3dsConfirm, data),

  orderCancel: (orderId) =>
    RequestAPI.post(ENDPOINT.orders.cancelByUser + `/${orderId}`),

  orderIdempotencyCancel: (data) =>
    RequestAPI.post(ENDPOINT.orders.cancelByUser, data),

  getOrders: (data) =>
    RequestAPI.post(ENDPOINT.orders.status, data, getLangHeaders()).then(
      (response: OrdersResponse) => ({
        ...response,
        orders: response.orders?.map(withNormalizedDeliveryMethod) ?? [],
      }),
    ),

  getOrder: (id) =>
    RequestAPI.get(
      ENDPOINT.orders.status + `/${id}`,
      {},
      getLangHeaders(),
    ).then((response: OrderResponse) => ({
      ...response,
      order: withNormalizedDeliveryMethod(response.order),
    })),

  getPayments: (data, deliveryZoneId) =>
    RequestAPI.get(ENDPOINT.orders.paymentMethods.list, {
      availablePaymentProviders: data,
      ...(deliveryZoneId && { deliveryZoneId }),
    }),

  addPayment: (data) =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.add, data),

  deletePayment: (data) =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.remove, data),

  createIntent: () =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.setupIntent),

  setDefaultPayment: (data) =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.setDefault, data),

  bindCard: (data) =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.bindCard, data),

  getBindCardVerification: (bindingUuid) =>
    RequestAPI.get(
      `${ENDPOINT.orders.paymentMethods.bindCard}/${bindingUuid}/verification`,
    ),

  checkSmsKey: (data) =>
    RequestAPI.post(ENDPOINT.orders.paymentMethods.bindCardCheckSmsKey, data),

  newFeedback: (data) => RequestAPI.post(ENDPOINT.orders.feedback.create, data),

  fillFeedback: (feedbackId, data) =>
    RequestAPI.post(ENDPOINT.orders.feedback.fill + feedbackId, data),

  checkPromocode: (promocode, options) =>
    RequestAPI.get(ENDPOINT.promocodes.get + promocode, undefined, options),

  getGifts: (warehouseCode) =>
    RequestAPI.get(ENDPOINT.gifts.getMechanisms + warehouseCode),

  payTip: (orderId, data) => RequestAPI.post(ENDPOINT.tips.pay + orderId, data),

  checkPayedTip: (orderId) => RequestAPI.get(ENDPOINT.tips.check + orderId),

  getCurrency: () => RequestAPI.get(ENDPOINT.orders.getCurrency),

  getPaymentSystems: (deliveryZoneId?: string) =>
    RequestAPI.get(
      company.hasPaymentSystemsV3
        ? ENDPOINT.payments.paymentSystemsV3
        : ENDPOINT.payments.paymentSystemsV2,
      {
        ...(deliveryZoneId && { deliveryZoneId }),
      },
    ),

  myfatoorahInit: () => RequestAPI.post(ENDPOINT.payments.myfatoorahInitialize),

  getOrdersWithPromocodes: (data) =>
    RequestAPI.get(ENDPOINT.orders.getOrdersWithPromocodes, data),
};
